import React, { useEffect, useState } from 'react';
import { X, Package, Calendar, Tag, ImageOff } from 'lucide-react';
import { resolveImageUrl } from '../utils/imageUrl';

const ItemDetailModal = ({ isOpen, onClose, item, onEdit, onFixImage }) => {
  const [imageBroken, setImageBroken] = useState(false);
  
  // Reset broken state when item changes
  useEffect(() => {
    setImageBroken(false);
  }, [item?.item_id]);
  
  // Handle Escape key to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  if (!isOpen || !item) return null;

  const categoryLabels = {
    cardstock: 'Cardstock',
    patterned_paper: 'Patterned Paper',
    die: 'Die Cut',
    stamp: 'Stamp',
    embellishment: 'Embellishment',
    sentiment: 'Sentiment',
    sticker: 'Sticker',
    other: 'Other'
  };

  return (
    <div 
      className="fixed inset-0 bg-charcoal/60 flex items-center justify-center z-[60] p-4" 
      onClick={onClose}
      data-testid="item-detail-modal"
    >
      <div 
        className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-beige p-6 flex items-center justify-between">
          <h2 className="text-2xl font-light text-charcoal">Item Details</h2>
          <button
            onClick={onClose}
            className="text-gray-soft hover:text-charcoal transition-colors duration-200"
            data-testid="close-detail-modal"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Image — matches Edit/Add styling (max-h-48, light border container) */}
          {item.image_url && !imageBroken && (
            <div>
              <label className="text-sm font-medium text-charcoal mb-2 block">Product Image</label>
              <div className="border border-beige rounded-lg p-4 text-center bg-cream/30">
                <img
                  src={resolveImageUrl(item.image_url)}
                  alt={item.name}
                  className="max-h-48 mx-auto rounded-lg"
                  onError={() => setImageBroken(true)}
                />
              </div>
            </div>
          )}

          {/* Broken Image State */}
          {item.image_url && imageBroken && (
            <div>
              <label className="text-sm font-medium text-charcoal mb-2 block">Product Image</label>
              <div className="border border-amber-200 bg-amber-50 rounded-lg p-6 flex flex-col items-center justify-center text-center">
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mb-2">
                  <ImageOff size={24} className="text-amber-600" />
                </div>
                <p className="text-amber-800 text-sm font-medium mb-1">Image Unavailable</p>
                <p className="text-amber-600 text-xs mb-3">This image seems to have a problem</p>
                <button
                  onClick={() => {
                    if (onFixImage) {
                      onFixImage();
                    } else {
                      onClose();
                      onEdit();
                    }
                  }}
                  className="px-4 py-2 bg-sage text-white text-sm rounded-lg hover:bg-sage-dark transition-all flex items-center gap-2"
                  data-testid="fix-image-modal-btn"
                >
                  <ImageOff size={14} />
                  Fix Image
                </button>
              </div>
            </div>
          )}

          {/* Colors — matches Edit styling */}
          {item.colors && item.colors.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-charcoal mb-3">
                Colors <span className="text-xs text-gray-soft font-normal ml-1">({item.colors.length})</span>
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {item.colors.map((color, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <div
                      className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg border-2 border-white shadow-md shrink-0"
                      style={{ backgroundColor: color }}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-soft">Color {idx + 1}</p>
                      <p className="text-xs sm:text-sm text-charcoal font-mono">{color}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-gray-soft mb-1">Name</p>
              <p className="text-base text-charcoal font-medium">{item.name}</p>
            </div>
            <div>
              <p className="text-xs text-gray-soft mb-1">Category</p>
              <p className="text-base text-charcoal">{categoryLabels[item.category] || item.category}</p>
            </div>
            {item.brand && (
              <div>
                <p className="text-xs text-gray-soft mb-1">Brand</p>
                <p className="text-base text-charcoal">{item.brand}</p>
              </div>
            )}
            <div>
              <p className="text-xs text-gray-soft mb-1">Added</p>
              <p className="text-base text-charcoal flex items-center space-x-1">
                <Calendar size={14} />
                <span>{new Date(item.created_at).toLocaleDateString()}</span>
              </p>
            </div>
          </div>

          {/* Description */}
          {item.description && (
            <div>
              <h3 className="text-sm font-medium text-charcoal mb-2">Description</h3>
              <p className="text-gray-soft text-sm leading-relaxed">{item.description}</p>
            </div>
          )}

          {/* User Notes */}
          {item.user_notes && (
            <div>
              <h3 className="text-sm font-medium text-charcoal mb-2">Notes</h3>
              <p className="text-gray-soft text-sm leading-relaxed">{item.user_notes}</p>
            </div>
          )}

          {/* Tags */}
          {item.tags && item.tags.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-charcoal mb-3 flex items-center space-x-2">
                <Tag size={16} />
                <span>Tags</span>
              </h3>
              <div className="flex flex-wrap gap-2">
                {item.tags.map((tag, idx) => (
                  <span 
                    key={idx} 
                    className="px-3 py-1 bg-cream text-charcoal text-sm rounded-full"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Sentiments */}
          {item.sentiments && item.sentiments.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-charcoal mb-3">Sentiments</h3>
              <div className="flex flex-wrap gap-2">
                {item.sentiments.map((sentiment, idx) => (
                  <span 
                    key={idx} 
                    className="px-3 py-1 bg-sage-light text-charcoal text-sm rounded-full border border-sage"
                  >
                    {sentiment}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end space-x-4 pt-4 border-t border-beige">
            <button
              onClick={onClose}
              className="px-6 py-3 border border-beige text-charcoal rounded-lg hover:bg-cream transition-all duration-200"
            >
              Close
            </button>
            <button
              onClick={() => {
                onClose();
                onEdit();
              }}
              className="px-6 py-3 bg-sage text-white rounded-lg hover:bg-sage-dark hover:scale-[1.02] active:scale-[0.98] transition-all duration-200"
            >
              Edit Item
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ItemDetailModal;